/**
 * ThemeSelector Component - Card-based Theme Selection
 * Provides clickable cards for light/dark/system theme selection
 * Following shadcn/ui design patterns and Tally-inspired styling
 * Enhanced with better visual feedback and accessibility
 */

import React from 'react';
import { useTheme } from './ThemeProvider';
import { Card, CardContent } from './ui/card';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { cn } from '../lib/utils';

interface ThemeOption {
  value: 'light' | 'dark' | 'system';
  label: string;
  description: string;
  icon: React.ReactNode;
}

const themeOptions: ThemeOption[] = [
  {
    value: 'light',
    label: 'Light',
    description: 'Clean and bright interface',
    icon: <Sun className="h-5 w-5" />,
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Easy on the eyes in low light',
    icon: <Moon className="h-5 w-5" />,
  },
  {
    value: 'system',
    label: 'System',
    description: 'Adapts to your OS preference',
    icon: <Monitor className="h-5 w-5" />,
  },
];

export const ThemeSelector: React.FC = () => {
  const { theme, setTheme } = useTheme();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {themeOptions.map((option) => (
          <Card
            key={option.value}
            className={cn(
              "relative cursor-pointer transition-all duration-300 hover:scale-[1.02]",
              "border-2 hover:border-primary/50 hover:shadow-lg",
              "focus-within:outline-none focus-within:ring-2 focus-within:ring-primary/20",
              theme === option.value 
                ? "border-primary bg-primary/5 ring-2 ring-primary/20 shadow-md" 
                : "border-border hover:bg-accent/5"
            )}
            onClick={() => setTheme(option.value)}
            role="button"
            tabIndex={0}
            aria-label={`Switch to ${option.label.toLowerCase()} theme`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setTheme(option.value);
              }
            }}
          >
            <CardContent className="p-6 text-center">
              <div className="flex flex-col items-center space-y-4">
                {/* Icon with enhanced styling */}
                <div className={cn(
                  "p-4 rounded-full transition-all duration-300",
                  "border-2 border-transparent",
                  theme === option.value
                    ? "bg-primary text-primary-foreground border-primary/20 shadow-sm"
                    : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"
                )}>
                  {option.icon}
                </div>

                {/* Label with improved typography */}
                <div className="space-y-2">
                  <h3 className={cn(
                    "font-semibold text-lg transition-colors",
                    theme === option.value ? "text-primary" : "text-foreground"
                  )}>
                    {option.label}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {option.description}
                  </p>
                </div>

                {/* Enhanced selected indicator */}
                {theme === option.value && (
                  <div className="absolute top-3 right-3 animate-in slide-in-from-top-2 duration-200">
                    <div className="bg-primary text-primary-foreground rounded-full p-1.5 shadow-sm">
                      <Check className="h-3 w-3" />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Enhanced theme preview indicator */}
      <div className="text-center p-4 bg-muted/30 rounded-lg border">
        <p className="text-sm text-muted-foreground">
          <span className="inline-flex items-center space-x-2">
            <span>Current theme:</span>
            <span className={cn(
              "font-medium capitalize px-2 py-1 rounded-md text-xs",
              "bg-primary/10 text-primary border border-primary/20"
            )}>
              {theme}
            </span>
          </span>
          {theme === 'system' && (
            <span className="block mt-1 text-xs opacity-75">
              Automatically using {typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'} mode
            </span>
          )}
        </p>
        
        {/* Visual theme preview dots */}
        <div className="flex justify-center items-center space-x-2 mt-3">
          <div className="w-3 h-3 rounded-full bg-background border-2 border-foreground/20"></div>
          <div className="w-2 h-2 rounded-full bg-primary"></div>
          <div className="w-3 h-3 rounded-full bg-accent"></div>
          <div className="w-2 h-2 rounded-full bg-muted-foreground"></div>
        </div>
      </div>
    </div>
  );
};

/**
 * Compact version for smaller spaces - Enhanced with better animations
 */
export const ThemeSelectorCompact: React.FC = () => {
  const { theme, setTheme } = useTheme();

  return (
    <div className="flex space-x-3 p-2 bg-muted/20 rounded-lg border">
      {themeOptions.map((option) => (
        <button
          key={option.value}
          onClick={() => setTheme(option.value)}
          className={cn(
            "relative p-3 rounded-lg border-2 transition-all duration-300",
            "hover:scale-110 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/20",
            "active:scale-95",
            theme === option.value
              ? "border-primary bg-primary text-primary-foreground shadow-sm"
              : "border-border bg-background hover:border-primary/50 hover:bg-primary/5"
          )}
          aria-label={`Switch to ${option.label.toLowerCase()} theme`}
          title={`${option.label} - ${option.description}`}
        >
          {option.icon}
          {theme === option.value && (
            <div className="absolute -top-1 -right-1">
              <div className="bg-primary text-primary-foreground rounded-full p-0.5">
                <Check className="h-2 w-2" />
              </div>
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

export default ThemeSelector;